import { useCallback, useSyncExternalStore } from 'react';
import { createLocalStore } from '../utils/localStore';

/** Per-video watch state, keyed by video slug. */
export interface VideoProgress {
    /** Seconds watched, as last reported by the player. */
    seconds: number;
    /** Total runtime in seconds, 0 until the player reports it. */
    duration: number;
    /** Explicitly marked complete, or watched past the completion threshold. */
    completed: boolean;
    /** Epoch ms of the last update — orders "Continue watching". */
    updatedAt: number;
}

export type VideoProgressMap = Record<string, VideoProgress>;

/** Past this fraction, the tail is credits/outro — treat the video as watched. */
const COMPLETION_RATIO = 0.95;

/** Below this, the person barely started; not worth a "Continue watching" slot. */
const RESUME_MIN_RATIO = 0.02;

const store = createLocalStore<VideoProgressMap>('odo_resources_video_progress', {});

function percentOf(progress: VideoProgress | undefined): number {
    if (!progress || progress.duration <= 0) return 0;
    if (progress.completed) return 100;
    return Math.min(100, Math.round((progress.seconds / progress.duration) * 100));
}

export interface UseVideoProgressResult {
    progress: VideoProgressMap;
    /** 0-100 for the given slug; 0 when never watched. */
    percentFor: (slug: string) => number;
    /** Records a player position. Marks complete past {@link COMPLETION_RATIO}. */
    report: (slug: string, seconds: number, duration: number) => void;
    /** Slugs with meaningful, unfinished progress, most recent first. */
    resumeOrder: () => string[];
}

export function useVideoProgress(): UseVideoProgressResult {
    const progress = useSyncExternalStore(store.subscribe, store.get, store.get);

    const report = useCallback((slug: string, seconds: number, duration: number) => {
        if (!slug || duration <= 0) return;
        const current = store.get()[slug];
        // The player polls every few seconds; skip writes that change nothing.
        if (current && Math.abs(current.seconds - seconds) < 1 && current.duration === duration) {
            return;
        }
        store.set({
            ...store.get(),
            [slug]: {
                seconds,
                duration,
                completed: current?.completed || seconds / duration >= COMPLETION_RATIO,
                updatedAt: Date.now()
            }
        });
    }, []);

    const percentFor = useCallback((slug: string) => percentOf(progress[slug]), [progress]);

    const resumeOrder = useCallback(
        () =>
            Object.entries(progress)
                .filter(
                    ([, entry]) =>
                        !entry.completed &&
                        entry.duration > 0 &&
                        entry.seconds / entry.duration >= RESUME_MIN_RATIO
                )
                .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
                .map(([slug]) => slug),
        [progress]
    );

    return { progress, percentFor, report, resumeOrder };
}
